<template>
  <div class="my-account">
    <div class="row">
      <div class="col-sm-12">
        <div class="box">
          <div class="box-header">
            <h1 class="box-title">My Account</h1>
          </div>
          <div class="box-body">
            <div class="nav-tabs-custom">
              <ul id="nav-tabs-wrapper" class="nav nav-tabs nav-tabs-horizontal">
                <li :class="{ 'active': activeTab == 'overview' }"><a href="#overview" data-toggle="tab" aria-expanded="true"><span class="fa fa-fw fa-info-circle"></span> Overview</a></li>
                <li :class="{ 'active': activeTab == 'change-password' }"><a href="#changePassword" data-toggle="tab" aria-expanded="false"><span class="fa fa-fw fa-key"></span> Change Password</a></li>
                <li :class="{ 'active': activeTab == 'contact-info' }"><a href="#editContactInfo" data-toggle="tab" aria-expanded="false"><span class="fa fa-fw fa-id-card-o"></span> Update Contact Info</a></li>
                <li :class="{ 'active': activeTab == 'driver-info' }"><a href="#editDriverInfo" data-toggle="tab" aria-expanded="false"><span class="fa fa-fw fa-car"></span> Update Driver Info</a></li>
                <li :class="{ 'active': activeTab == 'email-prefs' }"><a href="#emailPrefs" data-toggle="tab" aria-expanded="false"><span class="fa fa-fw fa-envelope"></span> Email Preferences</a></li>
                <li :class="{ 'active': activeTab == 'locations' }"><a href="#savedLocations" data-toggle="tab" aria-expanded="false"><span class="fa fa-fw fa-map-marker"></span> Saved Locations</a></li>
              </ul>
              <div class="tab-content">
                <div class="tab-pane fade in" :class="{ 'active': activeTab == 'overview' }" id="overview">
                  <template v-if="setAlternateLogin">
                    <br/>
                    <button class="btn btn-info" @click="changeUsernameModal()">Set Alternate Login</button>
                  </template>
                  <template v-if="restricted">
                    <br/>
                    <div class="alert alert-danger">
                      <h4><span class="fa fa-fw fa-exclamation-triangle"></span>Account Restricted</h4>
                      <p>Your account is currently restricted. You will still be charged any membership fees but will be unable to make any new reservations.</p>
                      <p>Call 1300 575 878 for more information.</p>
                    </div>
                  </template>
                  <template v-if="accountType.businessPlan">
                    <h4>Account</h4>
                    <strong>{{ businessName }}</strong><br/>
                    {{ businessAddress }}
                  </template>
                  <template v-if="!isMasterAccount">
                    <div class="row">
                      <div class="col-sm-6 col-md-4 col-lg-3">
                        <h4>Account Owner</h4>
                        <div class="panel panel-default driver-panel">
                          <div class="panel-body">
                            <strong>{{ masterAccount.fullName }} </strong>
                            <br/><br/>
                            <strong>Phone:</strong> {{ masterAccount.homePhone }}<br/>
                            <strong>Email:</strong> {{ masterAccount.email }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-if="isMasterAccount">
                    <div class="row">
                      <div class="col-xs-12">
                        <h3>Your Plan</h3>
                        <h4>{{ accountType.name }}</h4>
                        <div v-if="accountType.annualFee" class="price-block">
                          <span class="small-price-block-dollar">$</span>
                          <span class="small-price-block-price">{{ accountType.annualFee | currency_short }}</span>
                          <span class="small-price-block-per">/yr</span>
                        </div>
                        <span v-if="accountType.annualFee === '0.00'">No ongoing membership fees.</span>
                        <div v-if="accountType.monthlyFee" class="price-block">
                          <span class="small-price-block-dollar">$</span>
                          <span class="small-price-block-price">{{ accountType.monthlyFee | currency_short }}</span>
                          <span class="small-price-block-per">/mo</span>
                        </div>
                        <p v-if="accountType.monthlyFee" class="price-info">Billed biannually</p>
                        <p v-if="!accountType.businessPlan && !accountType.monthlyFee" class="price-info">&nbsp;</p>
                        <p v-if="accountType.driverCost" class="joining-fee">
                          ${{ accountType.driverCost | currency_short }} joining fee for additional drivers.
                        </p>
                        <template v-if="memberDiscount">
                          <br/>
                          <div class="alert alert-info">
                            <h4><span class="fa fa-fw fa-info"></span>Account Discount</h4>
                            <p>Your account currently has a permanent {{ memberDiscount.percentDiscount }}% discount on all reservation rates listed below.</p>
                            <p>This does not include any optional extras, parking levies, tolls, kilometres travelled or already discounted rates (overnight and weekend rates).</p>
                            <p v-if="memberDiscount.expiry">This discount will expire on {{ memberDiscount.expiry | short_date_format }}.</p>
                          </div>
                        </template>
                        <h4>Rates</h4>
                        <div class="row">
                          <div class="col-sm-6 col-lg-4">
                            <div class="rates">
                              <div class="rates-body">
                                <div class="rates-row-alt rates-row-header">
                                  <div class="rates-block rates-row-header"><strong>Rate</strong></div>
                                  <div class="rates-block rates-row-header" v-for="rate in rates">
                                    <strong>{{ rate.CarClass.name }}</strong>
                                  </div>
                                </div>
                                <div class="rates-row">
                                  <div class="rates-block"><strong>Hourly</strong></div>
                                  <div class="rates-block rates-row-header" v-for="rate in rates">
                                    <strong>{{ rate.hourlyRate | currency_round }}</strong>
                                  </div>
                                </div>
                                <div class="rates-row-alt">
                                  <div class="rates-block"><strong>Daily</strong>*</div>
                                  <div class="rates-block rates-row-header" v-for="rate in rates">
                                    <strong>{{ rate.dailyRate | currency_round }}</strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <br/>
                        <h4>Drivers</h4>
                        Drivers currently on account: {{ drivers.length + 1 }}<br/>
                        Max drivers allowed: {{ accountType.maxAccounts }} <span v-if="accountType.businessPlan">+</span><br/><br/>
                        <div class="row">
                          <template v-for="driver in drivers">
                            <div class="col-sm-6 col-md-4 col-lg-3">
                              <div class="panel panel-default driver-panel">
                                <div class="panel-body">
                                  <strong>{{ driver.fullName }} </strong>
                                  <br/>
                                  {{ driver.homePhone }}<br/>
                                  {{ driver.email }}<br/>
                                  <i class="red">{{ driver.applicationApproved ? '' : 'Pending approval' }}</i>
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>
                        <div class="row">
                          <div class="col-xs-12">
                            <button class="btn btn-default"
                            :disabled="accountType.maxAccounts == drivers.length + 1 &&
                            !accountType.businessPlan" @click="showAddDriver">Add Driver</button><br/>
                            <i v-if="accountType.maxAccounts == drivers.length + 1 &&
                            !accountType.businessPlan">Max drivers for account reached. Upgrade your plan to allow more drivers.</i>
                          </div>
                        </div>
                        <div class="row" v-if="addDriverShow" transition="expand">
                          <div class="col-sm-6">
                            <div class="panel panel-default">
                              <div class="panel-body">
                                <h5>Add Driver</h5>
                                <p>Input the email of the person you would like
                                  to add to your account below, they will be sent
                                  a link to follow to register as a member.</p>
                                <p>Once they register an account, they will
                                  automatically be added to your account as a
                                  driver but will not be able to make any
                                  reservations until they have been approved
                                  by our team.</p>
                                <hr class="add-driver"/>
                                <a class="interactive-cursor pull-right" @click="addDriverShow = false">[Hide]</a>
                                <strong>Email</strong>: <input type="text" v-model="emailRecip"/><button @click="addDriver(emailRecip)" class="btn btn-info btn-xs">Send</button>
                                <div class="alert alert-danger" v-show="emailError" transition="expand">{{ emailError }}</div>
                                <div class="alert alert-success" v-show="emailSuccess" transition="expand">{{ emailSuccess }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-xs-12">
                            <h4>Pre-Authorisation</h4>
                            <div v-if="accountType.businessPlan">
                              <p class="pre-authorisation">$0*</p>
                            </div>
                            <p class="pre-authorisation" v-if="!accountType.businessPlan">${{ accountType.preAuth | currency_short }}</p>
                            <br/>
                            <h4>Kilometers</h4>
                            <div class="small-price-block">
                              <span class="small-price-block-dollar">$</span>
                              <span class="small-price-block-price">{{ accountType.Rates[0].perKmCost }}</span>
                              <span class="small-price-block-per">/km</span>
                              <p class="rate-info">*All day bookings include {{ accountType.Rates[0].includeKms }}km</p>
                            </div>
                            <button class="btn btn-info" v-link="{ path: '/sign-up' }">View Other Plans</button>
                            <p class="joining-fee-info" v-if="accountType.businessPlan">* ${{ accountType.preAuth | currency_short }} Pre-authorisation for less than 4 drivers</p>
                            <hr/>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <template v-if="memberDiscount">
                      <br/>
                      <div class="alert alert-info">
                        <h4><span class="fa fa-fw fa-info"></span>Account Discount</h4>
                        <p>Your account currently has a permanent {{ memberDiscount.percentDiscount }}% discount on all reservation rates.</p>
                        <p>This does not include any optional extras, parking levies, tolls, kilometres travelled or already discounted rates (overnight and weekend rates).</p>
                        <p v-if="memberDiscount.expiry">This discount will expire on {{ memberDiscount.expiry | short_date_format }}.</p>
                      </div>
                    </template>
                    <h4>Rates</h4>
                    <div class="row">
                      <div class="col-sm-6 col-lg-4">
                        <div class="rates">
                          <div class="rates-body">
                            <div class="rates-row-alt rates-row-header">
                              <div class="rates-block rates-row-header"><strong>Rate</strong></div>
                              <div class="rates-block rates-row-header" v-for="rate in rates">
                                <strong>{{ rate.CarClass.name }}</strong>
                              </div>
                            </div>
                            <div class="rates-row">
                              <div class="rates-block"><strong>Hourly</strong></div>
                              <div class="rates-block rates-row-header" v-for="rate in rates">
                                <strong>{{ rate.hourlyRate | currency_round }}</strong>
                              </div>
                            </div>
                            <div class="rates-row-alt">
                              <div class="rates-block"><strong>Daily</strong>*</div>
                              <div class="rates-block rates-row-header" v-for="rate in rates">
                                <strong>{{ rate.dailyRate | currency_round }}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-xs-12">
                        <h4>Pre-Authorisation</h4>
                        <div v-if="accountType.businessPlan">
                          <p class="pre-authorisation">$0*</p>
                          <p class="joining-fee-info">* ${{ accountType.preAuth | currency_short }} Pre-authorisation if there are less than 4 drivers on the account</p>
                        </div>
                        <p v-if="!accountType.businessPlan" class="pre-authorisation">${{ accountType.preAuth | currency_short }}</p>
                        <h4>Kilometers</h4>
                        <div class="small-price-block">
                          <span class="small-price-block-dollar">$</span>
                          <span class="small-price-block-price">{{ accountType.Rates[0].perKmCost }}</span>
                          <span class="small-price-block-per">/km</span>
                          <p class="rate-info">*All day bookings include {{ accountType.Rates[0].includeKms }}km</p>
                        </div>
                        <hr/>
                      </div>
                    </div>
                  </template>
                  <h4>Deactivate Account</h4>
                  <button class="btn btn-danger" @click="deactivate()" v-if="!pending">Deactivate</button>
                  <div v-if="pending" class="alert alert-info">Your request to deactivate your account has been received and is pending approval.</div>
                </div>
                <div class="tab-pane fade in" :class="{ 'active': activeTab == 'change-password' }" id="changePassword">
                  <br/>
                  <div class="row">
                    <div class="col-xs-12">
                      <form class="form-horizontal" v-on:submit.prevent="changePassword">
                        <password-input v-bind:field.sync="currentPassword"></password-input>
                        <password-confirm-input v-bind:field.sync="passwordInput"></password-confirm-input>
                        <div class="row" v-show="passwordFormError" transition="expand">
                          <div class="col-sm-8 col-sm-offset-4">
                            <div class="form-section-danger">{{ passwordFormError }}</div>
                          </div>
                        </div>
                        <button class="btn btn-default pull-right" type="submit">Change</button>
                      </form>
                    </div>
                  </div>
                </div>
                <div class="tab-pane fade in" :class="{ 'active': activeTab == 'contact-info' }" id="editContactInfo">
                  <div class="row">
                    <div class="col-xs-12">
                      <br/>
                      <form v-if="loaded" class="form-horizontal" v-on:submit.prevent="updateContactInfo">
                        <div class="form-group">
                          <label class="col-sm-4 control-label">Username:</label>
                          <div class="col-sm-8 padding-top">
                            {{ username }}
                          </div>
                        </div>
                        <div class="form-group">
                          <label class="col-sm-4 control-label">Member ID:</label>
                          <div class="col-sm-8 padding-top">
                            {{ memberID }}
                          </div>
                        </div>
                      <email-input v-bind:field.sync="email"></email-input>
                      <text-input v-bind:field.sync="address"></text-input>
                      <text-input v-bind:field.sync="suburb"></text-input>
                      <postcode-input v-bind:field.sync="postcode"></postcode-input>
                      <object-select-input v-bind:field.sync="state"></object-select-input>
                      <phone-input v-bind:field.sync="homePhone"></phone-input>
                      <phone-input v-bind:field.sync="mobilePhone"></phone-input>
                      <phone-input v-bind:field.sync="workPhone"></phone-input>
                      <p class="form-section-title">Emergency Contact Details</p>
                      <text-input v-bind:field.sync="emergencyFirstName"></text-input>
                      <text-input v-bind:field.sync="emergencyLastName"></text-input>
                      <text-input v-bind:field.sync="emergencyPhone"></text-input>
                      <text-input v-bind:field.sync="emergencyRelation"></text-input>
                      <div class="row" v-show="contactFormError" transition="expand">
                        <div class="col-sm-8 col-sm-offset-4">
                          <div class="form-section-danger">{{ contactFormError }}</div>
                        </div>
                      </div>
                      <button class="btn btn-default pull-right" type="submit">Update</button>
                    </form>
                    </div>
                  </div>
                </div>
                <div class="tab-pane fade in" :class="{ 'active': activeTab == 'driver-info' }" id="editDriverInfo">
                  <div class="row">
                    <div class="col-xs-12">
                      <br/>
                      <form v-if="loaded" class="form-horizontal" v-on:submit.prevent="updateDriverInfo">
                        <amazon-media-input v-bind:field.sync="license"></amazon-media-input>
                        <amazon-media-input v-bind:field.sync="licenseBack"></amazon-media-input>
                        <text-input v-bind:field.sync="driversLicense"></text-input>
                        <object-select-input v-bind:field.sync="driversLicenseIssued"></object-select-input>
                        <text-input v-bind:field.sync="driversLicenseCountry"></text-input>
                        <date-input v-bind:field.sync="driversLicenseExpiry"></date-input>
                        <template v-if="student">
                          <p class="form-section-title">Student Details</p>
                          <amazon-media-input v-bind:field.sync="proofOfEnrolment"></amazon-media-input>
                          <date-input v-bind:field.sync="studentExpiry"></date-input>
                        </template>
                        <template v-if="accountPromoUsed">
                          <p class="form-section-title">Proof of Address Details</p>
                          <amazon-media-input v-bind:field.sync="proofOfAddressPromocode"></amazon-media-input>
                        </template>
                        <div class="row" v-show="driverInfoError" transition="expand">
                          <div class="col-sm-8 col-sm-offset-4">
                            <div class="form-section-danger">{{ driverInfoError }}</div>
                          </div>
                        </div>
                        <button class="btn btn-default pull-right" type="submit">Update</button>
                      </form>
                    </div>
                  </div>
                </div>
                <div class="tab-pane fade in" :class="{ 'active': activeTab == 'email-prefs' }" id="emailPrefs">
                  <div class="row">
                    <div class="col-xs-12">
                      <br/>
                      <br/>
                      <form v-el:email-prefs v-if="loaded" class="form-horizontal" v-on:submit.prevent="updateEmailPrefs">
                        <template v-for="field in emailPrefs">
                          <checkboxes-input v-bind:field.sync="field"></checkboxes-input>
                        </template>
                        <button class="btn btn-default pull-right" type="submit">Save</button>
                      </form>
                    </div>
                  </div>
                </div>
                <div class="tab-pane fade in" :class="{ 'active': activeTab == 'locations' }" id="savedLocations">
                  <div class="row">
                    <br/>
                    <template v-for="location in locations">
                      <div class="col-sm-6 col-md-4 col-lg-3">
                        <div class="panel panel-default location-panel expand">
                          <div class="panel-body">
                            <strong>{{ location.locationName }} </strong>
                            <span class="fa fa-lg2 fa-trash red pull-right interactive-cursor" @click="deleteLocationModal(location)"></span>
                            <span class="fa fa-lg2 fa-edit pull-right interactive-cursor" @click="editLocationModal(location)"></span>
                            <br/>
                            {{ location.value }}
                          </div>
                        </div>
                      </div>
                    </template>
                    <div class="col-sm-6 col-md-4 col-lg-3">
                      <div class="panel panel-add interactive-cursor location-panel expand" @click="addLocationModal">
                        <div class="panel-body">
                          <p class="add-location"><strong>Add <span class="fa fa-lg2 fa-plus pull-right interactive-cursor"></span></strong></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Edit Location Modal -->
    <modal v-if="editLocationModalActive" v-el:edit-location-modal>
      <template slot="header">
        Edit Location
      </template>
      <template slot="body">
        <form class="form-horizontal">
        <text-input v-bind:field.sync="locationName"></text-input>
        <address-input v-bind:field.sync="locationAddress"></address-input>
          <div class="row" v-show="addressError" transition="expand">
            <div class="col-sm-8 col-sm-offset-4">
              <div class="form-section-danger">{{ addressError }}</div>
            </div>
          </div>
        </form>
      </template>
      <template slot="footer">
        <button type="button" class="btn btn-info pull-left" data-dismiss="modal">Cancel</button>
        <button class="btn btn-default" @click="location('editLocation', 'put')">Save</button>
      </template>
    </modal>
    <!-- New Location Modal -->
    <modal v-if="addLocationModalActive" v-el:add-location-modal>
      <template slot="header">
        Add Location
      </template>
      <template slot="body">
        <form class="form-horizontal">
        <text-input v-bind:field.sync="locationName"></text-input>
        <address-input v-bind:field.sync="locationAddress"></address-input>
          <div class="row" v-show="addressError" transition="expand">
            <div class="col-sm-8 col-sm-offset-4">
              <div class="form-section-danger">{{ addressError }}</div>
            </div>
          </div>
        </form>
      </template>
      <template slot="footer">
        <button type="button" class="btn btn-info pull-left" data-dismiss="modal">Cancel</button>
        <button class="btn btn-default" @click="location('addLocation', 'post')">Add</button>
      </template>
    </modal>
    <!-- Delete Location Modal -->
    <modal v-if="deleteModalActive" v-el:delete-location-modal>
      <template slot="header">
        Delete Location
      </template>
      <template slot="body">
        <p>Are you sure you wish to delete this location?<br/>
        (This action cannot be undone)</p>
        <div class="row">
          <div class="col-sm-6">
            <div class="panel panel-default location-panel expand">
              <div class="panel-body">
                <strong>{{ locationToDelete.locationName }} </strong>
                <br/>
                {{ locationToDelete.value }}
              </div>
            </div>
          </div>
        </div>
      </template>
      <template slot="footer">
        <button type="button" class="btn btn-info pull-left" data-dismiss="modal">Cancel</button>
        <button class="btn btn-danger" @click="location('deleteLocation', 'delete')">Delete</button>
      </template>
    </modal>
    <!-- Deactivate Account Modal -->
    <modal v-if="deactivateModalActive" v-el:deactivate-modal>
      <template slot="header">
        Deactivate Account
      </template>
      <template slot="body">
        Please provide your reason for deactivating your account below:<br/>
        <textarea v-model="reason"></textarea>
      </template>
      <template slot="footer">
        <button type="button" class="btn btn-info pull-left" data-dismiss="modal">Cancel</button>
        <button class="btn btn-danger" @click="sendDeactivateRequest()">Deactivate</button>
      </template>
    </modal>
    <!-- Success Modal -->
    <modal v-if="successModalActive" v-el:success-modal>
      <template slot="header">
        Success
      </template>
      <template slot="body">
        {{{ successModalMessage }}}
      </template>
    </modal>
    <!-- Change Username Modal -->
    <modal v-if="changeUsernameModalActive" v-el:change-username-modal>
      <template slot="header">
        Set Alternate Login
      </template>
      <template slot="body">
        <p>If you wish to choose an alternative name to login to your account with,
        please enter it below and click submit. </p>
        <p>You will still be able to log in using your Member ID.</p>
        <p><u>Please note you will only be able to set this alternative username
          <strong>ONCE</strong> so whatever you choose will be final.</u></p>
        <form class="form-horizontal">
        <text-input v-bind:field.sync="newUsername"></text-input>
          <div class="row" v-show="usernameError" transition="expand">
            <div class="col-sm-8 col-sm-offset-4">
              <div class="form-section-danger">{{ usernameError }}</div>
            </div>
          </div>
        </form>
      </template>
      <template slot="footer">
        <button type="button" class="btn btn-info pull-left" data-dismiss="modal">Cancel</button>
        <button class="btn btn-default" @click="changeUsernameConfirm()">Submit</button>
      </template>
    </modal>
    <!-- Confirm Change Username Modal -->
    <modal v-if="confirmChangeUsernameModalActive" v-el:confirm-change-username-modal>
      <template slot="header">
        Confirm
      </template>
      <template slot="body">
        Are you sure you wish to use <strong>{{ newUsername.value }}</strong> as your alternate login? You will not be able to change this again.
      </template>
      <template slot="footer">
        <button type="button" class="btn btn-info pull-left" data-dismiss="modal">No</button>
        <button class="btn btn-default" @click="changeUsername()">Yes</button>
      </template>
    </modal>
    <!-- Error Modal -->
    <error-modal v-if="errorModalActive" v-bind:error-message="errorMessage"></error-modal>
  </div>
</template>

<script>
  import Modal from './Modal.vue'
  import ErrorModal from './modals/ErrorModal.vue'
  import PasswordConfirmInput from './inputs/PasswordConfirmInput.vue'
  import PasswordInput from './inputs/PasswordInput.vue'
  import TextInput from './inputs/TextInput.vue'
  import PostcodeInput from './inputs/PostcodeInput.vue'
  import EmailInput from './inputs/EmailInput.vue'
  import PhoneInput from './inputs/PhoneInput.vue'
  import NumberInput from './inputs/NumberInput.vue'
  import AmazonMediaInput from './inputs/AmazonMediaInput.vue'
  import ObjectSelectInput from './inputs/ObjectSelectInput.vue'
  import DateInput from './inputs/DateInput.vue'
  import AddressInput from './inputs/AddressInput.vue'
  import CheckboxesInput from './inputs/CheckboxesInput.vue'
  export default {
    name: 'MyAccount',
    components: {
      TextInput,
      Modal,
      CheckboxesInput,
      ErrorModal,
      PasswordConfirmInput,
      PasswordInput,
      PostcodeInput,
      EmailInput,
      PhoneInput,
      NumberInput,
      AmazonMediaInput,
      ObjectSelectInput,
      DateInput,
      AddressInput
    },
    data () {
      return {
        confirmChangeUsernameModalActive: false,
        changeUsernameModalActive: false,
        pending: false,
        emailPrefs: [],
        businessAddress: '',
        businessName: '',
        referenceNum: '',
        addLocationModalActive: false,
        locationAddress: {},
        locationName: {},
        emailRecip: '',
        editLocationModalActive: false,
        successModalActive: false,
        reason: '',
        accountType: {
          Rates: [
            {
              perKmCost: 0,
              includeKms: 0
            }
          ]
        },
        locationToDelete: {},
        passwordInput: {},
        passwordFormError: false,
        contactFormError: false,
        driverInfoError: false,
        address: {},
        emergencyFirstName: {},
        emergencyRelation: {},
        emergencyLastName: {},
        emergencyPhone: {},
        email: {},
        homePhone: {},
        licenseBack: {},
        driversLicenseExpiry: {},
        license: {},
        driversLicense: {},
        mobilePhone: {},
        driversLicenseCountry: {},
        postcode: {},
        proofOfEnrolment: {},
        proofOfAddressPromocode: {},
        state: {},
        driversLicenseIssued: {},
        suburb: {},
        workPhone: {},
        currentPassword: {},
        student: false,
        accountPromoUsed: false,
        loaded: false,
        locations: [],
        locationModalActive: false,
        locationProp: {},
        isEdit: false,
        lat: -37.81656469809118,
        long: 144.9674615263939,
        deleteModalActive: false,
        rates: [],
        errorModalActive: false,
        deactivateModalActive: false,
        drivers: [],
        isMasterAccount: false,
        addDriverShow: false,
        emailRegex: /^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/,
        emailError: false,
        emailSuccess: false,
        masterAccount: {},
        restricted: false,
        setAlternateLogin: false,
        studentExpiry: {},
        newUsername: {
          name: 'username',
          label: 'Username',
          required: true,
          value: '',
          confirmation: true,
          valid: false,
          dirty: false
        },
        usernameError: false,
        username: '',
        memberID: '',
        successModalMessage: false,
        activeTab: '',
        memberDiscount: false
      }
    },
    ready () {
      this.getUsername()
      this.getAccountType()
      this.initForms()
      this.getLocation()
      this.getDrivers()
      this.getResetToken()
      this.activeTab = this.$route.query.tab || 'overview'
    },
    methods: {
      changeUsernameModal () {
        this.changeUsernameModalActive = true
        this.$nextTick(function () {
          $(this.$els.changeUsernameModal).modal()
          $(this.$els.changeUsernameModal).on('hidden.bs.modal', () => {
            this.changeUsernameModalActive = false
            this.usernameError = false
          })
        })
      },
      changeUsernameConfirm () {
        this.usernameError = false
        let isRegex = new RegExp('^(?!.*\\.{2}.*)[\\w\\.\\@-]+$').test(this.newUsername.value)
        if (isRegex) {
          this.confirmChangeUsernameModalActive = true
          this.$nextTick(function () {
            $(this.$els.confirmChangeUsernameModal).modal()
            $(this.$els.confirmChangeUsernameModal).on('hidden.bs.modal', () => {
              this.confirmChangeUsernameModalActive = false
            })
          })
        } else {
          this.usernameError = 'Username may only contain letters, numbers, single consecutive full stops, @ and underscores.'
        }
      },
      changeUsername () {
        this.usernameError = false
        this.$http.post(`${this.$route.authType}/change-username`, { username: this.newUsername.value }).then((response, status, request) => {
          if (response.data.usernameTaken) {
            $(this.$els.confirmChangeUsernameModal).modal('hide')
            this.usernameError = 'This username is already in use. Please select a unique username.'
          } else {
            window.sessionStorage.setItem('token', response.data.token)
            this.$root.loggedIn = true
            $(this.$els.changeUsernameModal).modal('hide')
            $(this.$els.confirmChangeUsernameModal).modal('hide')
            this.successModalActive = true
            this.successModalMessage = `New login is: ${this.newUsername.value}`
            this.$nextTick(function () {
              $(this.$els.successModal).modal()
              $(this.$els.successModal).on('hidden.bs.modal', () => {
                this.getResetToken()
                this.successModalMessage = false
                this.successModalActive = false
              })
            })
            this.setAlternateLogin = false
            this.getUsername()
          }
        })
      },
      showAddDriver () {
        this.emailError = false
        this.emailSuccess = false
        this.emailRecip = ''
        this.addDriverShow = true
      },
      addDriver (email) {
        this.emailError = false
        this.emailSuccess = false
        if (new RegExp(this.emailRegex, 'gi').test(email)) {
          let authType = this.$route.authType
          this.$http.post(`${authType}/add-driver`, { email }).then((response, status, request) => {
            this.emailSuccess = 'Sent!'
          })
        } else {
          this.emailError = 'Please enter a valid email.'
        }
      },
      getDrivers () {
        let authType = this.$route.authType
        this.$http.get(`${authType}/get-drivers`, {}).then((response, status, request) => {
          this.drivers = response.data.drivers
          this.isMasterAccount = response.data.isMasterAccount
          this.masterAccount = response.data.masterAccount
          this.businessName = response.data.businessName
          this.businessAddress = response.data.businessAddress
          this.restricted = response.data.restricted
        })
      },
      sendDeactivateRequest () {
        let authType = this.$route.authType
        this.$http.post(`${authType}/deactivate`, { reason: this.reason }).then((response, status, request) => {
          this.referenceNum = response.data
          this.reason = ''
          $(this.$els.deactivateModal).modal('toggle')
          this.successModalActive = true
          this.successModalMessage = `<p>An email was sent to our team to approve
          your request. You will receive a confirmation email shortly. Please
          note your account has <strong>not</strong> yet been deactivated, your
          request is under review and you will be notified when your account has
          been deactivated. You will still be able to log in in the meantime</p>
          <p>Your reference number is ${this.referenceNum}.</p>
          <hr/>
          <h4>Contact</h4><p><strong>T</strong> &nbsp;&nbsp;1300 575 878<br/>
          <strong>E</strong> &nbsp;&nbsp;
          <a href="mailto:info@greensharecar.com.au">info@greensharecar.com.au
          </a>`
          this.$nextTick(function () {
            $(this.$els.successModal).modal()
            $(this.$els.successModal).on('hidden.bs.modal', () => {
              this.getResetToken()
              this.successModalMessage = false
              this.successModalActive = false
            })
          })
        })
      },
      deactivate () {
        let authType = this.$route.authType
        this.$http.get(`${authType}/deactivate/check-balance`, {}).then((response, status, request) => {
          if (response.data < 0) {
            this.errorModal(`Your account cannot be deactivated until your
              account balance has been paid off. Please visit the billing section
              to pay your outstanding balance of $${Math.abs(response.data)}
              then try again.`)
          } else {
            this.deactivateModalActive = true
            this.$nextTick(function () {
              $(this.$els.deactivateModal).modal()
              $(this.$els.deactivateModal).on('hidden.bs.modal', () => {
                this.deactivateModalActive = false
              })
            })
          }
        })
      },
      getAccountType () {
        let authType = this.$route.authType
        this.$http.get(`${authType}/account-type`, {}).then((response, status, request) => {
          this.accountType = response.data.accountType
          this.rates = response.data.accountType.Rates
          response.data.accountType.Rates.forEach(rate => {
            if (rate.premiumRate) {
              this.premRates = rate
            } else {
              this.econRates = rate
            }
          })
        })
      },
      getResetToken () {
        let authType = this.$route.authType
        this.$http.get(`${authType}/get-reset-token`, {}).then((response, status, request) => {
          this.pending = response.data.pending
          this.setAlternateLogin = response.data.setAlternateLogin
        })
      },
      getUsername () {
        let authType = this.$route.authType
        this.$http.get(`${authType}/get-username`, {}).then((response, status, request) => {
          this.username = response.data.username
          this.memberID = response.data.memberID
          this.memberDiscount = response.data.memberDiscount
        })
      },
      errorModal (error) {
        this.errorMessage = error
        this.errorModalActive = true
        this.$nextTick(function () {
          $('#errorModal').modal()
          $('#errorModal').on('hidden.bs.modal', () => {
            this.errorModalActive = false
          })
        })
      },
      initForms () {
        let authType = this.$route.authType
        this.loaded = false
        this.$http.get(`${authType}/edit-my-account`, {}).then((response, status, request) => {
          this.address = response.data.inputs.address
          this.currentPassword = response.data.inputs.currentPassword
          this.emergencyFirstName = response.data.inputs.emergencyFirstName
          this.emergencyRelation = response.data.inputs.emergencyRelation
          this.emergencyLastName = response.data.inputs.emergencyLastName
          this.emergencyPhone = response.data.inputs.emergencyPhone
          this.email = response.data.inputs.email
          this.homePhone = response.data.inputs.homePhone
          this.licenseBack = response.data.inputs.licenseBack
          this.driversLicenseExpiry = response.data.inputs.driversLicenseExpiry
          this.license = response.data.inputs.license
          this.driversLicense = response.data.inputs.driversLicense
          this.mobilePhone = response.data.inputs.mobilePhone
          this.driversLicenseCountry = response.data.inputs.driversLicenseCountry
          this.passwordInput = response.data.inputs.password
          this.postcode = response.data.inputs.postcode
          this.proofOfEnrolment = response.data.inputs.proofOfEnrolment
          this.proofOfAddressPromocode = response.data.inputs.proofOfAddressPromocode
          this.studentExpiry = response.data.inputs.studentExpiry
          this.state = response.data.inputs.state
          this.driversLicenseIssued = response.data.inputs.driversLicenseIssued
          this.emailPrefs = response.data.inputs.emailPrefs
          this.suburb = response.data.inputs.suburb
          this.workPhone = response.data.inputs.workPhone
          this.student = response.data.student
          this.accountPromoUsed = response.data.accountPromoUsed
          this.locations = response.data.locations
          this.locationAddress = {
            name: 'location_address',
            type: 'address',
            label: 'Address',
            value: '',
            confirmation: true,
            valid: false,
            dirty: false,
            latitude: 0,
            longitude: 0,
            required: true,
            uuid: ''
          }
          this.locationName = {
            name: 'name',
            label: 'Name',
            required: false,
            value: '',
            confirmation: true,
            valid: false,
            dirty: false
          }
          this.$nextTick(function () {
            this.loaded = true
          })
        })
      },
      changePassword () {
        this.passwordFormSuccess = false
        if (this.passwordInput.valid && this.passwordInput.dirty && this.currentPassword.valid && this.currentPassword.dirty) {
          let authType = this.$route.authType
          let checkPassword = this.currentPassword.value
          this.$http.post(`${authType}/check-password`, { checkPassword }).then((response, status, request) => {
            if (response.body === 'true') {
              let password = this.passwordInput.value
              this.$http.put(`${authType}/form`, { password, permalink: 'editPassword' }).then((response, status, request) => {
                window.sessionStorage.setItem('token', response.data.result)
                this.$root.loggedIn = true
                this.passwordFormError = false
                this.successModalActive = true
                this.successModalMessage = 'Password changed successfully.'
                this.initForms()
                this.$nextTick(function () {
                  $(this.$els.successModal).modal()
                  $(this.$els.successModal).on('hidden.bs.modal', () => {
                    this.successModalMessage = false
                    this.successModalActive = false
                  })
                })
              })
            } else {
              this.passwordFormError = 'Current password is incorrect'
            }
          })
        } else {
          this.passwordFormError = 'You did not fill all fields correctly'
        }
      },
      updateEmailPrefs () {
        let authType = this.$route.authType
        let emailPrefs = $(this.$els.emailPrefs).serializeObject()
        this.$http.put(`${authType}/form`, {
          emailPrefs,
          permalink: 'editEmailPreferences'
        }).then((response, status, request) => {
          this.successModalActive = true
          this.successModalMessage = 'Email preferences updated successfully.'
          this.initForms()
          this.$nextTick(function () {
            $(this.$els.successModal).modal()
            $(this.$els.successModal).on('hidden.bs.modal', () => {
              this.successModalMessage = false
              this.successModalActive = false
            })
          })
        })
      },
      updateContactInfo () {
        let valid =
        this.email.valid &&
        this.address.valid &&
        this.suburb.valid &&
        this.postcode.valid &&
        this.state.valid &&
        this.homePhone.valid &&
        this.mobilePhone.valid &&
        this.workPhone.valid &&
        this.emergencyFirstName.valid &&
        this.emergencyLastName.valid &&
        this.emergencyPhone.valid &&
        this.emergencyRelation.valid
        if (valid) {
          let authType = this.$route.authType
          this.$http.put(`${authType}/form`, {
            email: this.email.value,
            address: this.address.value,
            suburb: this.suburb.value,
            postcode: this.postcode.value,
            state: this.state.value,
            homePhone: this.homePhone.value,
            mobilePhone: this.mobilePhone.value,
            workPhone: this.workPhone.value,
            emergencyFirstName: this.emergencyFirstName.value,
            emergencyLastName: this.emergencyLastName.value,
            emergencyPhone: this.emergencyPhone.value,
            emergencyRelation: this.emergencyRelation.value,
            permalink: 'editContactDetails'
          }).then((response, status, request) => {
            this.contactFormError = false
            this.successModalActive = true
            this.successModalMessage = 'Details updated successfully.'
            this.initForms()
            this.$nextTick(function () {
              $(this.$els.successModal).modal()
              $(this.$els.successModal).on('hidden.bs.modal', () => {
                this.successModalMessage = false
                this.successModalActive = false
              })
            })
          })
        } else {
          this.contactFormError = 'You did not fill all fields correctly'
        }
      },
      updateDriverInfo () {
        let valid =
        this.license.valid &&
        this.licenseBack.valid &&
        this.driversLicense.valid &&
        this.driversLicenseIssued.valid &&
        this.driversLicenseCountry.valid &&
        this.driversLicenseExpiry.valid
        if (this.student) {
          valid = valid && this.proofOfEnrolment.valid && this.studentExpiry.valid
        }
        if (this.accountPromoUsed) {
          valid = valid && this.proofOfAddressPromocode.valid
        }
        if (valid) {
          let authType = this.$route.authType
          this.$http.put(`${authType}/form`, {
            license: this.license.value,
            licenseBack: this.licenseBack.value,
            driversLicense: this.driversLicense.value,
            driversLicenseIssued: this.driversLicenseIssued.value,
            driversLicenseCountry: this.driversLicenseCountry.value,
            driversLicenseExpiry: this.driversLicenseExpiry.value,
            proofOfEnrolment: this.proofOfEnrolment.value,
            proofOfAddressPromocode: this.proofOfAddressPromocode.value,
            studentExpiry: this.studentExpiry.value,
            permalink: 'editDriverDetails'
          }).then((response, status, request) => {
            this.driverInfoError = false
            this.successModalActive = true
            this.successModalMessage = 'Details updated successfully.'
            this.initForms()
            this.$nextTick(function () {
              $(this.$els.successModal).modal()
              $(this.$els.successModal).on('hidden.bs.modal', () => {
                this.successModalMessage = false
                this.successModalActive = false
              })
            })
          })
        } else {
          this.driverInfoError = 'You did not fill all fields correctly'
        }
      },
      deleteLocationModal (location) {
        this.deleteModalActive = true
        this.locationToDelete = location
        this.$nextTick(function () {
          $(this.$els.deleteLocationModal).modal()
          $(this.$els.deleteLocationModal).on('hidden.bs.modal', () => {
            this.deleteModalActive = false
          })
        })
      },
      editLocationModal (location) {
        this.locationAddress = location
        this.locationName.value = location.locationName
        this.editLocationModalActive = true
        this.$nextTick(function () {
          $(this.$els.editLocationModal).modal()
          this.$broadcast('modal-opened')
          $(this.$els.editLocationModal).on('hidden.bs.modal', () => {
            this.initForms()
            this.editLocationModalActive = false
          })
        })
      },
      addLocationModal () {
        this.locationAddress.latitude = this.lat
        this.locationAddress.longitude = this.long
        this.addLocationModalActive = true
        this.$nextTick(function () {
          $(this.$els.addLocationModal).modal()
          this.$broadcast('modal-opened')
          $(this.$els.addLocationModal).on('hidden.bs.modal', () => {
            this.initForms()
            this.addLocationModalActive = false
          })
        })
      },
      location (permalink, method) {
        let authType = this.$route.authType
        let params = { address: this.locationAddress, name: this.locationName.value, permalink }
        if (method === 'delete') {
          params = { body: { locationUuid: this.locationToDelete.uuid, permalink } }
        }
        this.$http[method](`${authType}/form`, params).then((response, status, request) => {
          $(this.$els[permalink + 'Modal']).modal('toggle')
          this.initForms()
        })
      },
      getLocation () {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(position => {
            this.lat = position.coords.latitude
            this.long = position.coords.longitude
          })
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// MyAccount.vue?2c2cb7ec